import React, { useState, useMemo, useEffect } from 'react'
import { Popup, Search, Cell } from 'react-vant'
import { useTranslation } from 'react-i18next'
import { getCountryList, type CountryOption } from '@/utils/countryData'

interface CountrySelectorProps {
    value: string
    onChange: (value: string, countryId?: number) => void
    placeholder?: string
    options?: Array<{ value: string; label: string }> // 可选，如果不传则使用内置数据
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
    value,
    onChange,
    placeholder,
    options
}) => {
    const { t } = useTranslation()
    const [visible, setVisible] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [countryList, setCountryList] = useState<CountryOption[]>([])

    // 加载国家列表
    useEffect(() => {
        if (!options) {
            getCountryList().then(countries => {
                setCountryList(countries)
            })
        }
    }, [options])

    // 使用传入的 options 或内置的 countryList
    const finalOptions = useMemo(() => {
        return options || countryList
    }, [options, countryList])

    // 获取选中的国家（通过 label 匹配，因为现在传递的是 label）
    const selectedCountry = useMemo(() => {
        return finalOptions.find(option => option.label === value)
    }, [finalOptions, value])

    // 过滤国家列表
    const filteredCountries = useMemo(() => {
        if (!searchText.trim()) {
            return finalOptions
        }
        const searchLower = searchText.toLowerCase()
        return finalOptions.filter(country =>
            country.label.toLowerCase().includes(searchLower) ||
            country.value.toLowerCase().includes(searchLower)
        )
    }, [finalOptions, searchText])

    // 处理选择
    const handleSelect = (country: CountryOption | { value: string; label: string }) => {
        const countryId = 'id' in country ? country.id : undefined
        // 传递 label（显示名称）而不是 value（国家代码）
        onChange(country.label, countryId)
        setVisible(false)
        setSearchText('')
    }

    return (
        <>
            <div
                onClick={() => setVisible(true)}
                className="w-full px-4 h-11.5 border-[0.5px] border-[#808080] border-solid rounded-2.5 bg-white cursor-pointer flex items-center justify-between"
            >
                <span className={`text-3 ${selectedCountry ? 'text-black' : 'text-[#1A1A1A]/30'}`}>
                    {selectedCountry ? selectedCountry.label : placeholder}
                </span>
                <img src="/icon/select-down.svg" alt="" />
            </div>

            <Popup
                visible={visible}
                onClose={() => {
                    setVisible(false)
                    setSearchText('')
                }}
                position="bottom"
                round
                style={{ height: '70%' }}
            >
                <div className="flex flex-col h-full">
                    {/* 标题 */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <span
                            className="text-sm text-gray-500 cursor-pointer"
                            onClick={() => {
                                setVisible(false)
                                setSearchText('')
                            }}
                        >
                            {t('取消')}
                        </span>
                        <span className="text-base font-medium">{t('请选择国家或地区')}</span>
                        <span className="w-12"></span>
                    </div>

                    {/* 搜索框 */}
                    <Search
                        value={searchText}
                        onChange={setSearchText}
                        placeholder={t('搜索国家')}
                        shape="round"
                    />

                    {/* 国家列表 */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredCountries.length > 0 ? (
                            filteredCountries.map((country) => (
                                <Cell
                                    key={country.value}
                                    title={country.label}
                                    clickable
                                    onClick={() => handleSelect(country)}
                                    className={value === country.label ? 'bg-blue-50' : ''}
                                    rightIcon={value === country.label ? <span className="text-blue-600">✓</span> : null}
                                />
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                <div className="text-sm">{t('未找到匹配的国家')}</div>
                            </div>
                        )}
                    </div>
                </div>
            </Popup>
        </>
    )
}

export default CountrySelector
